import { useMemo } from "react";

import { ClockRewind } from "components/icons/generated";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult } from "utils/access";

import { StackContext } from "../Context";
import { showStackStateHistoryRollBackConfirmationModal } from "./RollbackConfirmationModal";

type StackStateHistoryRollBackActionProps = {
  onRollback: () => void;
  isCurrentState: boolean;
  loading: boolean;
  canManagedStateRollbackStack: AccessResult;
};

const lockTooltip = "Please lock the stack first.";
const currentStateTooltip = "You cannot roll back the current state.";

const StackStateHistoryRollBackAction = ({
  onRollback,
  isCurrentState,
  loading,
  canManagedStateRollbackStack,
}: StackStateHistoryRollBackActionProps) => {
  const { stack } = useTypedContext(StackContext);

  const actionIsDisabled =
    !canManagedStateRollbackStack.result || !stack.lockedBy || isCurrentState;

  const onModalShow = () => {
    showStackStateHistoryRollBackConfirmationModal({ onConfirm: onRollback });
  };

  const tooltipMessage = useMemo(() => {
    if (!canManagedStateRollbackStack.result) {
      return canManagedStateRollbackStack.message;
    }
    if (canManagedStateRollbackStack.result && isCurrentState) {
      return currentStateTooltip;
    }
    if (!stack.lockedBy && canManagedStateRollbackStack.result && !isCurrentState) {
      return lockTooltip;
    }

    return undefined;
  }, [
    canManagedStateRollbackStack.result,
    canManagedStateRollbackStack.message,
    isCurrentState,
    stack.lockedBy,
  ]);

  if (!tooltipMessage && actionIsDisabled) {
    return null;
  }

  return (
    <Tooltip
      active={actionIsDisabled}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          startIcon={ClockRewind}
          onPress={onModalShow}
          disabled={actionIsDisabled}
          loading={loading}
          fullWidth
        >
          Roll back
        </Button>
      )}
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default StackStateHistoryRollBackAction;
